import { Box, Button, Typography } from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
    CatalogItemsSelectedCounter,
    getItemDisplayLabelForCount,
} from 'entities/CatalogItem';
import { getSelectedCatalogItems } from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { getSelectedPropertyCatalogId } from 'entities/Property/model/selectors/PropertySelectors';
import { getAddCatalogItemIsLoading } from 'features/AddCatalogItem/model/selectors/addCatalogItemSelectors';
import { addCatalogItemService } from 'features/AddCatalogItem/model/services/addCatalogItemService';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { DialogBoxContent } from 'shared/ui/DialogBoxContent/DialogBoxContent';
import { Loader } from 'shared/ui/Loader/Loader';

import { CatalogItemCategoryFormBody } from '../AddCatalogItem/CatalogItemCategoryFormBody';

interface FormProps {
    category: string;
}

interface AddCatalogItemDialogFormProps {
    onClose: () => void;
}

export const AddCatalogItemDialogForm = ({
    onClose,
}: AddCatalogItemDialogFormProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const selectedItems = useSelector(getSelectedCatalogItems);
    const isLoading = useSelector(getAddCatalogItemIsLoading);
    const catalogId = useSelector(getSelectedPropertyCatalogId);

    const itemDisplayLabel = useMemo(
        () => getItemDisplayLabelForCount(Object.values(selectedItems), t),
        [selectedItems, t],
    );

    const handleSubmit = async (values: FormProps): Promise<void> => {
        const data = {
            items: Object.keys(selectedItems).map((itemUuid) => ({
                itemUuid,
                categoryId: values.category,
            })),
        };
        await dispatch(
            addCatalogItemService({
                data,
                catalogId,
            }),
        );
    };

    return (
        <Formik onSubmit={handleSubmit} initialValues={{ category: '' }}>
            {(props: FormikProps<FormProps>) => {
                return (
                    <Form>
                        <DialogBoxContent
                            onClose={onClose}
                            buttons={
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    width="100%"
                                >
                                    <CatalogItemsSelectedCounter />
                                    <Box>
                                        <Button onClick={onClose}>
                                            {t('Cancel')}
                                        </Button>
                                        <Button
                                            disabled={
                                                !props.values.category ||
                                                isLoading
                                            }
                                            startIcon={
                                                isLoading && (
                                                    <Loader color="inherit" />
                                                )
                                            }
                                            variant="contained"
                                            type="submit"
                                        >
                                            {t('Add')}
                                        </Button>
                                    </Box>
                                </Box>
                            }
                        >
                            <Box data-testid="add-new-category-title">
                                <Typography
                                    typography={'poppins.subtitle2'}
                                    marginBottom={
                                        itemDisplayLabel ? '16px' : '18px'
                                    }
                                >
                                    {t('Select Category')}
                                </Typography>
                                {itemDisplayLabel ? (
                                    <Typography
                                        data-testid="add-catalog-item-dialog-item-name"
                                        typography="openSans.body2"
                                        marginBottom="36px"
                                        sx={{ color: '#363636' }}
                                    >
                                        {itemDisplayLabel}
                                    </Typography>
                                ) : null}
                                <CatalogItemCategoryFormBody />
                            </Box>
                        </DialogBoxContent>
                    </Form>
                );
            }}
        </Formik>
    );
};
